/*
 * Copyright (c) 2019
 * Company: G-Portal
 * Filename: server.ts
 * Class: server.ts
 * Author: Karl Magerl<karl@g-portal.com>
 * Version: 0.1.2
 */

import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { connect } from 'socket.io-client';
import { API } from '../../api/api';
import { IStartGameserverResponse } from '../../gamecloud/interface';
import $ from "jquery";


/**
 * <h2>Server class for Game- and Voiceservers</h2>
 *
 * this is the documentation about the frontend module to get informations about servers.
 * You can find every customer gameserver out-of-the-Box inside of window.getServer()
 * and operate with them.
 *
 * For not calling the query engine to often, the server class also provides a currently really simple
 * caching functionality with a key-value-store, ttl which will be automatically used instead of sending
 * multiple requests for the name, ip, playercount etc.
 *
 * <h3>Gameserver Methods</h3>
 *
 * <ul>
 * <li>[[Gameserver.getName]]</li>
 * <li>[[Gameserver.getPlayer]]</li>
 * <li>[[Gameserver.getIp]]</li>
 * <li>[[Gameserver.getServerId]]</li>
 * <li>[[Gameserver.start]]</li>
 * <li>[[Gameserver.stop]]</li>
 * <li>[[Gameserver.restart]]</li>
 * <li>[[Gameserver.reinstall]]</li>
 * <li>[[Gameserver.delete]]</li>
 * <li>[[Gameserver.query]]</li>
 * <li>[[Gameserver.isInFocus]]</li>
 * <li>[[Gameserver.state]]</li>
 * <li>[[Gameserver.getSettingsLink]]</li>
 * </ul>
 *
 * <h3>Voiceserver Methods</h3>
 *
 * <ul>
 * <li>[[Voiceserver.isOnline]]</li>
 * <li>[[Voiceserver.getServerId]]</li>
 * <li>[[Voiceserver.start]]</li>
 * <li>[[Voiceserver.restart]]</li>
 * <li>[[Voiceserver.reinstall]]</li>
 * <li>[[Voiceserver.delete]]</li>
 * <li>[[Voiceserver.query]]</li>
 * <li>[[Voiceserver.isInFocus]]</li>
 * <li>[[Voiceserver.state]]</li>
 * <li>[[Voiceserver.getSettingsLink]]</li>
 * </ul>
 *
 * <h2>Basic Usage</h2>
 *
 * ```typescript
 * let server : ServerInterface = window.getServerByServiceId(1);
 * if(server !== null) {
 *  switch(server.type()) {
 *    case ServerType.VOICESERVER:
 *      server.getName()
 *        .then((name : string) => {
 *          console.log(name);
 *        })
 *        .catch((reason : any) => {
 *          console.log(reason);
 *        });
 *
 *      break;
 *
 *     case ServerType.GAMESERVER:
 *        // ...
 *       break;
 *  }
 * }
 * ```
 *
 * <h3>Get current server from gameserverdetail</h3>
 *
 * This example demonstrates how to get a server from the customer that is currently
 * visiting a gameserver detail page, here we are restarting a server (voiceserver or gameserver)
 * when the customer clicks on a button with the data-attribute ```data-action='action'```.
 *
 * The getFocusServer() is a window method registered by the server class.
 *
 * ```typescript
 * let server : ServerInterface = window.getFocusedServer();
 *
 * if(server !== null) {
 *    // the customer is on a gameserver page
 *    $(document).on("button[data-action='restart']", 'click', (event) => {
 *      server.restart()
 *        .then((state : boolean) => {
 *          if(state === true) {
 *            console.log('server restarted');
 *          }
 *        })
 *        .catch(error) => {
 *          console.log(error);
 *        });
 *    });
 * }
 * ```
 *
 * <h3>Get a list of servers and print the ip</h3>
 *
 * This will append the ip address of every server into a specific list
 * with jQuery.
 *
 * ```typescript
 * let serverList = window.getServers();
 *
 * if(serverList.length > 0) {
 *  serverList.forEach((server : ServerInterface) => {
 *    server.getIp()
 *      .then((ipAddress : string) => {
 *        $('#ip-list .placeholder').append(ipAddress);
 *      })
 *      .catch((error) => {
 *        console.log(error);
 *      });
 *  });
 * }
 * ```
 *
 *
 * <h3>Bind server specific state with button</h3>
 *
 * The server lib will automatically update the state of a gameserver without the need
 * of taking care to listen to a websocket event. You can find a list of possible states at [[ServerStatus]].
 *
 * ```typescript
 * let server = window.getFocusedServer();
 *
 * server.options.stateChangedCallback = (state : ServerStatus) => {
 *    if(state !== ServerStatus.RUNNING) {
 *      $("button[data-type='stop']").attr('disabled', 'disabled');
 *    }
 *    else {
 *      $("button[data-type='stop']").removeAttr('disabled');
 *    }
 * };
 * ```
 */
export namespace Server {
  import construct = Reflect.construct;

  export interface ServerInterface {
    serviceId: number;
    serverId: number;
    state: ServerStatus;
    options: ServerOptions;
    type: ServerType;
    stateEvent: ServerEventHandler<IStateEvent>;
    lastQueried: Date;
    cluster: boolean;
    links: Record<string, any>;
    clusterServer: ServerInterface[];

    isInFocus(): boolean;

    getName(): Promise<string>;

    getIp(): Promise<string>;

    restart(restartUrl: string): Promise<boolean>;

    stop(stopUrl: string): Promise<boolean>;

    start(startUrl: string): Promise<boolean>;

    query(): Promise<GameServerQueryResponse | VoiceServerQueryResponse>;

    delete(deleteUrl: string): Promise<boolean>;

    dispatchCustomEvent(type, id, data): void;

    reinstall(reinstallUrl: string): Promise<boolean>;
  }

  interface IWebsocketState {
    details: IStateDetails;
    state: number;
  }

  export interface IStateEvent {
    newstate: ServerStatus;
    oldstate: ServerStatus;
    details: IStateDetails;
  }

  export interface IStateChange {
    state: number;
    message: string | null;
  }

  export interface IStateDetails {
    action: string | null;
    percentage: number | null;
  }

  interface ICache<T> {
    isValid(): boolean;

    getValue(): T;
  }

  /**
   * interface for delete responses
   */
  export interface IDeleteServerResponse {
    message: string;
    result: boolean;
  }

  export class ServerEventHandler<T> {
    private _callbackList: Array<(event: T) => void> = [];

    public addEventListener(eventType: ServerEventType, callback: (event: T) => void): void {
      this._callbackList.push(callback);
    }

    public getEventListener(): Array<(event: T) => void> {
      return this._callbackList;
    }

    /**
     *
     * @param eventData
     */
    public dispatchEvent(eventData: T): void {
      if (this._callbackList.length) {
        this._callbackList.forEach((callback) => {
          // Utilities.debug(`executing ${callback.name}`);

          callback.call(null, eventData);
        });
      }
    }
  }

  /**
   * a enum for the different known server socket types
   */
  enum ServerSocketTypes {
    SENSORS = 'sensors',
    STATECHANGE = 'stateChanged'
  }

  /**
   * a enum for the different servertypes
   */
  export enum ServerType {
    GAMESERVER = 'gameserver',
    VOICESERVER = 'voiceserver',
    NODESERVER = 'nodeserver'
  }

  class Cache<T> implements ICache<T> {
    /**
     * when was the cache created in milliseconds
     *
     * @type {number}
     * @private
     */
    private _created = Date.now();

    private _value: T;

    private _lifeTime: number;

    /**
     * this property is used for filling up the cache
     * if the value is dead
     * @type {<T>}
     */
    private _cacheCallback: () => T;

    get cacheCallback(): () => T {
      return this._cacheCallback;
    }

    set cacheCallback(value: () => T) {
      this._cacheCallback = value;
    }

    get created(): number {
      return this._created;
    }

    set created(value: number) {
      this._created = value;
    }

    get value(): T {
      return this._value;
    }

    set value(value: T) {
      this._value = value;
    }

    get lifeTime(): number {
      return this._lifeTime;
    }

    set lifeTime(value: number) {
      this._lifeTime = value;
    }

    /**
     *
     * @param {T} value
     * @param {number} lifeTime
     * number of milliseconds how long the cache should be up
     *
     * @param cacheCallback
     */
    constructor(value: T, lifeTime: number, cacheCallback: () => T) {
      this.value = value;
      this.lifeTime = lifeTime;
      this.cacheCallback = cacheCallback;
    }

    /**
     * checks if the cache is still valid, returns false if not
     *
     * @returns {boolean}
     */
    public isValid(): boolean {
      return (Date.now() - this.lifeTime < this.created);
    }

    /**
     * returns the generic value
     *
     * @returns {T}
     */
    public getValue(): T {
      return this.value;
    }
  }

  /**
   * this class takes care about the serverstatus
   */
  export class ServerStatus {
    public static readonly LOADING: ServerStatus = new ServerStatus(1337, 'loading');

    public static readonly PREINSTALL: ServerStatus = new ServerStatus(0, 'preInstall');

    public static readonly INSTALLING: ServerStatus = new ServerStatus(1, 'install');

    public static readonly SUSPENDED: ServerStatus = new ServerStatus(2, 'suspended');

    public static readonly STOPPED: ServerStatus = new ServerStatus(3, 'stopped');

    public static readonly STOPPING: ServerStatus = new ServerStatus(4, 'stopping');

    public static readonly STARTING: ServerStatus = new ServerStatus(5, 'starting');

    public static readonly RUNNING: ServerStatus = new ServerStatus(6, 'running');

    public static readonly UPDATING: ServerStatus = new ServerStatus(7, 'updating');

    public static readonly REMOVING: ServerStatus = new ServerStatus(98, 'removing');

    public static readonly MAINENANCE: ServerStatus = new ServerStatus(99, 'maintenance');

    public static readonly UNKNOWN: ServerStatus = new ServerStatus(100, 'unknown');

    private _stateName: string;

    private _stateId: number;

    public static readonly stateList: ServerStatus[] = [
      ServerStatus.LOADING,
      ServerStatus.PREINSTALL,
      ServerStatus.INSTALLING,
      ServerStatus.SUSPENDED,
      ServerStatus.STOPPED,
      ServerStatus.STOPPING,
      ServerStatus.STARTING,
      ServerStatus.RUNNING,
      ServerStatus.UPDATING,
      ServerStatus.REMOVING,
      ServerStatus.MAINENANCE,
      ServerStatus.UNKNOWN,
    ];

    get stateName(): string {
      return this._stateName;
    }

    set stateName(value: string) {
      this._stateName = value;
    }

    get stateId(): number {
      return this._stateId;
    }

    set stateId(value: number) {
      this._stateId = value;
    }

    /**
     *
     * @param {number} stateId
     * @param {string} stateName
     */
    constructor(stateId: number, stateName: string) {
      this.stateName = stateName;
      this.stateId = stateId;
    }

    /**
     * will return a {@link ServerStatus} object if it finds the id,
     * if not it will just return null
     *
     * @param {number} id
     * @returns {ServerStatus | null}
     */
    public static getStateById(id: number): ServerStatus | null {
      let serverStatus: ServerStatus = null;

      ServerStatus.stateList.some((_serverStatus: ServerStatus) => {
        if (_serverStatus.stateId === id) {
          serverStatus = _serverStatus;
          return true;
        }
      });

      return serverStatus;
    }

    /**
     * will return a {@link ServerStatus} object if it finds the name,
     * if not it will just return null
     *
     * @param {string} state
     * @returns {ServerStatus | null}
     */
    public static getStateByName(state: string): ServerStatus | null {
      let serverStatus: ServerStatus = null;

      ServerStatus.stateList.some((_serverStatus: ServerStatus) => {
        if (_serverStatus.stateName.toLowerCase() === state.toLowerCase()) {
          serverStatus = _serverStatus;
          return true;
        }
      });

      return serverStatus;
    }

    /**
     * will return true or false if the status is valid or not
     *
     * @param {number} id
     * @returns {boolean}
     */
    public static isValidStatus(id: number): boolean {
      let exists = false;

      ServerStatus.stateList.some((_serverStatus: ServerStatus) => {
        if (_serverStatus.stateId === id) {
          exists = true;
          return true;
        }
      });

      return exists;
    }
  }

  export enum ServerEventType {
    STATECHANGE_EVENT = 'statechange',
    SERVERQUERY_EVENT = 'serverquery',
    GAMESERVERQUERY_EVENT = 'gameserverquery',
    GAMESERVERINITIALISED_EVENT = 'gameserverinit'
  }

  /**
   * this class should be used for every gameserver,
   * see {@link ServerInterface} for the most basic functionality
   * if you want to use it for your own classes
   *
   * @beta
   */
  export class ServerUnit implements ServerInterface {
    /**
     * Multiton which stores every gameserver
     * @type {ServerInterface[]}
     * @private
     */
    private static _instances = [];

    private _lastQueried: Date = null;

    private _serverId: number;

    public _serviceId: number;

    private _options: ServerOptions;

    private _type: ServerType;

    private _cluster = false;

    private _links: Record<string, any> = null;

    private _clusterServer: ServerInterface[] = [];

    private _state: ServerStatus = ServerStatus.getStateByName('unknown');

    private _optionAutoQueryTimerId: number = null;

    private _stateEvent: ServerEventHandler<IStateEvent> = new ServerEventHandler();

    get links(): Record<string, any> {
      return this._links;
    }

    set links(value: Record<string, any>) {
      this._links = value;
    }

    get clusterServer(): Server.ServerInterface[] {
      return this._clusterServer;
    }

    set clusterServer(value: Server.ServerInterface[]) {
      this._clusterServer = value;
    }

    get serverId(): number {
      return this._serverId;
    }

    set serverId(value: number) {
      this._serverId = value;
    }

    get cluster(): boolean {
      return this._cluster;
    }

    set cluster(value: boolean) {
      this._cluster = value;
    }

    get lastQueried(): Date {
      return this._lastQueried;
    }

    set lastQueried(value: Date) {
      this._lastQueried = value;
    }

    get stateEvent(): ServerEventHandler<IStateEvent> {
      return this._stateEvent;
    }

    set stateEvent(value: ServerEventHandler<IStateEvent>) {
      this._stateEvent = value;
    }

    get optionAutoQueryTimerId(): number {
      return this._optionAutoQueryTimerId;
    }

    set optionAutoQueryTimerId(value: number) {
      this._optionAutoQueryTimerId = value;
    }

    getIp(): Promise<string> {
      return undefined;
    }

    getName(): Promise<string> {
      return undefined;
    }

    get state(): Server.ServerStatus {
      return this._state;
    }

    /**
     * sets the state, if changed it will automatically call the {@link ServerOptions}
     * change callback
     *
     * @param {ServerStatus} value
     */
    set state(value: Server.ServerStatus) {
      if (value !== this.state && typeof this.options.stateChangedCallback === 'function') this.options.stateChangedCallback(value);

      this._state = value;
    }

    get type(): Server.ServerType {
      return this._type;
    }

    set type(value: Server.ServerType) {
      this._type = value;
    }

    static get instances(): ServerInterface[] {
      return this._instances;
    }

    static set instances(value: ServerInterface[]) {
      this._instances = value;
    }

    get options(): ServerOptions {
      return this._options;
    }

    set options(value: ServerOptions) {
      this._options = value;
      this.reloadOptions();
    }

    get serviceId() {
      return this._serviceId;
    }

    set serviceId(value: number) {
      this._serviceId = value;
    }

    public static getSelectedGameserver(): ServerUnit {
      return null;
    }

    /**
     *
     * @param {number} serviceId
     * @param {ServerOptions} options
     * @param {ServerType} type
     * @param {Object} links
     * @param {number} serverId
     * if the gameserver is currently displayed in the frontend
     */
    public constructor(
      {
        serviceId,
        serverId,
        type = ServerType.GAMESERVER,
        links,
        options = new ServerOptions(false, 0),
      }:
        {
          serviceId: number;
          serverId: number;
          type?: ServerType;
          links: Record<string, any>;
          options?: ServerOptions;
        },
    ) {
      this.serverId = serverId;
      this.serviceId = serviceId;
      this.options = options;
      this.type = type;
      this.links = links;

      if (this.options !== null) {
        this._checkOptions();
      }
    }

    /**
     *
     */
    public reloadOptions(): void {
      this._checkOptions();
    }

    /**
     * checks the specific options and sets them directly here
     * @private
     */
    private _checkOptions(): void {
      if (this.options.isAutoQueryOn() && this.options.isPlausibleTimer()) {
        const instance: ServerUnit = this;

        /* check if there is already a timer for this specific server, if yes clear it */
        if (this.optionAutoQueryTimerId !== null) {
          window.clearInterval(this.optionAutoQueryTimerId);
        }

        this.optionAutoQueryTimerId = window.setInterval(() => {
          let visibilityCheck = false;

          if (instance.options.isLazyQueryActive()) {
            visibilityCheck = true;
          }

          if (!visibilityCheck || visibilityCheck && (instance.options.visibiltyOption.isVisible === true || instance.options.visibiltyOption.wasVisible === true)) {
            /* reset the visibilty option was visible because it was checked and perhaps its not more visible */
            if (visibilityCheck && instance.options.visibiltyOption !== null) {
              instance.options.visibiltyOption.wasVisible = false;
            }
            const response: Promise<GameServerQueryResponse | VoiceServerQueryResponse> = instance.query();

            instance.options.queryEvent.dispatchEvent(response);
          }
        },

        this.options.autoQueryTimer);

        window.addEventListener('beforeunload', (ev) => {
          if (this.optionAutoQueryTimerId !== null) {
            Utilities.debug('canceling server query due to beforeunload event!');

            window.clearInterval(this.optionAutoQueryTimerId);
          }
        });
      }
    }

    /**
     * trys to restart a service
     * @returns {boolean}, it can return null if the state is completly unknown
     * because something critical happend
     */
    public async restart(restartUrl: string): Promise<boolean> {
      let result: boolean = null;

      await Axios.post(restartUrl, {}, Utilities.getAjaxJSONOptions())
        .then((response: AxiosResponse<IStartGameserverResponse>) => {
          result = response.status === 200 && response.data.result === null;
        });

      return result;
    }

    /**
     * trys to stop a service
     * @returns {boolean}, it can return null if the state is completly unknown
     * because something critical happend
     */
    public async stop(stopUrl: string): Promise<boolean> {
      if (stopUrl == null) throw new Error('No url for server stop provided!');
      let result: boolean = null;

      await Axios.post(stopUrl, {}, Utilities.getAjaxJSONOptions())
        .then((response: AxiosResponse<IStartGameserverResponse>) => {
          result = response.status === 200 && response.data.result === true;
        });

      return result;
    }

    /**
     * trys to stop a service
     * @returns {boolean}, it can return null if the state is completly unknown
     * because something critical happend
     */
    public async start(startUrl: string): Promise<boolean> {
      if (startUrl == null) throw new Error('No url for server start provided!');
      let result: boolean = null;

      await Axios.post(startUrl, {}, Utilities.getAjaxJSONOptions())
        .then((response: AxiosResponse<IStartGameserverResponse>) => {
          result = response.status === 200 && response.data.result === true;
        });

      return result;
    }

    /**
     * trys to reinstall a service
     * @returns {boolean}, it can return null if the state is completly unknown
     * because something critical happend
     */
    public async reinstall(reinstallUrl: string): Promise<boolean> {
      if (reinstallUrl == null) throw new Error('No url for server reinstall provided!');
      if (this.type !== ServerType.GAMESERVER) throw new NotImplementedMethodError('Not implemented!');

      let result: boolean = null;

      await Axios.post(reinstallUrl, {}, Utilities.getAjaxJSONOptions())
        .then((response: AxiosResponse<IStartGameserverResponse>) => {
          result = response.status === 200 && response.data.result === null;
        });

      return result;
    }

    /**
     * trys to delete a service
     * @returns {boolean}, it can return null if the state is completly unknown
     * because something critical happend
     */
    public async delete(deleteUrl: string): Promise<boolean> {
      if (deleteUrl == null) throw new Error('No url for server delete provided!');
      let result: boolean = null;

      await Axios.post(deleteUrl, {}, Utilities.getAjaxJSONOptions())
        .then((response: AxiosResponse<IStartGameserverResponse>) => {
          result = response.status === 200 && response.data.result === true;
        });

      return result;
    }

    /**
     * will query informations from the server,
     * will throw a {@link QueryResponseError} if the webrequest is failing
     *
     * @throws {QueryResponseError}
     * @returns {GameServerQueryResponse|VoiceServerQueryResponse}
     */
    public query(): Promise<GameServerQueryResponse | VoiceServerQueryResponse> {
      let queryResult: Promise<GameServerQueryResponse | VoiceServerQueryResponse> = null;

      switch (this.type) {
        case ServerType.GAMESERVER:
          queryResult = new Promise<GameServerQueryResponse | null>((resolve, reject) => {
            Axios.get(this._parseQueryUrl())
              .then((response: AxiosResponse<GameServerQueryResponse>) => {
                this.lastQueried = new Date();

                resolve(response.data);
              })
              .catch(() => {
                reject(new QueryResponseError(`Error: Query call failed for serverId ${this.serverId}!`));
              });
          });

          break;

        case ServerType.VOICESERVER:
          queryResult = new Promise<VoiceServerQueryResponse | null>((resolve, reject) => {
            Axios.post(this._parseQueryUrl())
              .then((response: AxiosResponse<VoiceServerQueryResponse>) => {
                this.lastQueried = new Date();

                resolve(response.data);
              })
              .catch(() => {
                reject(new QueryResponseError(`Error: Query call failed for serverId ${this.serverId}!`));
              })
              .then(() => {
                // console.log('finally');
              });
          });
          break;
      }

      return queryResult;
    }

    /**
     * returns the parsed query url for getting information about the server
     * @returns {string}
     * @private
     */
    protected _parseQueryUrl(): string {
      let queryUrl: string = null;

      switch (this.type) {
        case ServerType.GAMESERVER:
          queryUrl = window.gamesServerApiQuery.concat(this.serverId.toString());
          break;

        case ServerType.VOICESERVER:
          queryUrl = `${window.basePath + window.regionPrefix}/server/teamspeak/${this.serverId.toString()}/getLiveData`;
          break;
      }

      return queryUrl;
    }

    /**
     * returns if the customer got gameservers or not
     * @returns {boolean}
     */
    public static hasGameservers(): boolean {
      return ServerUnit.instances.length > 0;
    }

    /**
     *
     * @return {Array<ServerInterface>}
     */
    public static getServerInViewPort(): Array<ServerInterface> {
      const serverList: Array<ServerInterface> = [];

      if (!this.hasGameservers()) return null;

      this.instances.forEach((server) => {
        if (server.options !== null && server.options.isLazyQueryActive()) {
          if (server.options.visibiltyOption.isVisible) serverList.push(server);
        }
      });

      return serverList;
    }

    /**
     * returns the count of gameserver
     *
     * @returns {number}
     */
    public static getGameserverCount(): number {
      const count = 0;

      if (!ServerUnit.hasGameservers()) {
        return count;
      }

      return ServerUnit.instances.length;
    }

    /**
     * checks if the gameserver is currently open in the browser (whatever url)
     *
     * @returns {boolean}
     */
    public isInFocus(): boolean {
      if ('serverUnitSelectedId' in window && typeof window.serverUnitSelectedId !== 'undefined') {
        return this.serviceId === window.serverUnitSelectedId;
      }

      return false;
    }

    /**
     * gets the currently active, open gameserver
     *
     * @returns {ServerUnit|null}
     */
    public static getActiveServer(): ServerInterface | null {
      let gameserver: ServerInterface = null;

      ServerUnit.instances.forEach((gameserverUnit: ServerInterface) => {
        if (gameserverUnit.isInFocus()) gameserver = gameserverUnit;
      });

      return gameserver;
    }

    /**
     * will search for the specified serviceId, if it finds the server it will return
     * a [[ServerInterface]], if not it will just return null
     *
     * @param serviceId
     */
    public static getServerByServiceId(serviceId: number): ServerInterface | null {
      let server: ServerInterface = null;

      ServerUnit.instances.forEach((serverUnit: ServerInterface) => {
        if (serverUnit.serviceId === serviceId) {
          server = serverUnit;
        }
      });

      return server;
    }

    public static getServerByServiceId2(serviceId: number): Promise<ServerInterface> {
      return new Promise((resolve, reject) => {
        ServerUnit.instances.forEach((serverUnit: ServerInterface) => {
          if (serverUnit.serviceId === serviceId) {
            resolve(serverUnit);
          } else if (serverUnit.cluster) {
            serverUnit.clusterServer.forEach((su) => {
              if (su.serviceId === serviceId) {
                resolve(su);
              }
            });
          }
        });
      });
    }

    public static _websocketConnected() {
      Utilities.debug('websocket connected');
    }

    /*
     * Stati abfragen aus Redis (für Services):
     -> Status von Service mit <sid>: redis-cli HGET service.<sid> state
     -> alle Werte für Service mit <sid>: redis-cli HGETALL service.<sid>
     -> Sensordaten für Service mit <sid>: redis-cli PSUBSCRIBE service.<sid>.sensors

     ## Redis DBs
     */

    /**
     * for the old procedure
     *
     * @deprecated
     * should be removed asap
     */
    public dispatchCustomEvent(type, id, data) {
      const event = new CustomEvent('socketData', { detail: { type, id, data } });
      window.dispatchEvent(event);
    }
  }

  export class Gameserver extends ServerUnit {
    private _queryCache: Cache<Promise<GameServerQueryResponse>> = null;

    get queryCache(): Cache<Promise<GameServerQueryResponse>> {
      return this._queryCache;
    }

    set queryCache(value: Cache<Promise<GameServerQueryResponse>>) {
      this._queryCache = value;
    }

    async reinstall(reinstallUrl: string): Promise<boolean> {
      return super.reinstall(reinstallUrl);
    }

    public async query(): Promise<GameServerQueryResponse> {
      let queryResult: Promise<GameServerQueryResponse> = null;

      if (this.queryCache !== null && this.queryCache.isValid()) {
        return this.queryCache.getValue();
      }
      queryResult = new Promise<GameServerQueryResponse | null>((resolve, reject) => {
        Axios.get(this._parseQueryUrl())
          .then((response: AxiosResponse<GameServerQueryResponse>) => {
            resolve(response.data);
          })
          .catch(() => {
            reject(new QueryResponseError(`Error: Query call failed for serverId ${this.serverId}!`));
          });
      });

      this.queryCache = new Cache(queryResult, 5000, this.query);
      // this.queryCache.value = queryResult;

      return queryResult;
    }

    /**
     * queries the name from the response of a query call,
     * if there is no name he will just return null
     *
     * @throws QueryInvalidTypeError
     * @returns {string|null}
     */
    public getName(): Promise<string | null> {
      if (this.type !== ServerType.GAMESERVER) throw new QueryInvalidTypeError('Your servertype does not support this method!');

      const query: Promise<GameServerQueryResponse> = this.query();

      return query
        .then((value: GameServerQueryResponse) => value.name, (reason: any) => null)
        .catch((reason: any) => null);
    }

    /**
     * queries the current players from the response of a query call,
     * if there is no ip he will just return null
     *
     * @throws QueryInvalidTypeError
     * @returns {string | null}
     */
    public getPlayer(): Promise<number> {
      const query: Promise<GameServerQueryResponse> = this.query();

      return query
        .then((value: GameServerQueryResponse) => value.currentPlayers, (reason: any) => null)
        .catch((reason: any) => null);
    }

    /**
     * queries the ip from the response of a query call,
     * if there is no ip he will just return null
     *
     * @throws QueryInvalidTypeError
     * @returns {string | null}
     */
    public getIp(): Promise<string> {
      const query: Promise<GameServerQueryResponse> = this.query();

      return query
        .then((value: GameServerQueryResponse) => value.ipAddress, (reason: any) => null)
        .catch((reason: any) => null);
    }
  }

  /**
   * this class can be used to specify custom options when initializing a server
   */
  abstract class Config {
    private static readonly _CACHE_LIFETIME: number = 20000;

    static get getCacheLifetime(): number {
      return this._CACHE_LIFETIME;
    }
  }

  export class Voiceserver extends ServerUnit {
    private _queryCache: Cache<Promise<VoiceServerQueryResponse>> = null;

    /**
     * not implemented in the query engine for voiceservers
     *
     * @throws NotImplementedMethodError
     */
    public getIp(): Promise<string> {
      throw new NotImplementedMethodError('This method is not yet implemented for voiceservers');
    }

    /**
     * not implemented for voiceservers
     *
     * @throws NotImplementedMethodError
     */
    async reinstall(): Promise<boolean> {
      throw new NotImplementedMethodError('This method is not yet implemented for voiceservers');
    }

    /**
     * queries the slots from the response of a query call,
     * if there is no ip he will just return null
     *
     * @throws QueryInvalidTypeError
     * @returns {string | null}
     */
    public getSlots(): Promise<number> {
      const query: Promise<VoiceServerQueryResponse> = this.query();

      return query
        .then((value: VoiceServerQueryResponse) => Number.parseInt(value.slots), (reason: any) => null)
        .catch((reason: any) => null);
    }

    /**
     * queries if the voiceserver is uip,
     * if there is no ip he will just return null
     *
     * @throws QueryInvalidTypeError
     * @returns {boolean | null}
     */
    public isOnline(): Promise<boolean | null> {
      const query: Promise<VoiceServerQueryResponse> = this.query();

      return query
        .then((value: VoiceServerQueryResponse) => value.online === 1, (reason: any) => null)
        .catch((reason: any) => null);
    }

    get queryCache(): Cache<Promise<VoiceServerQueryResponse>> {
      return this._queryCache;
    }

    set queryCache(value: Cache<Promise<VoiceServerQueryResponse>>) {
      this._queryCache = value;
    }

    public query(): Promise<VoiceServerQueryResponse> {
      let queryResult: Promise<VoiceServerQueryResponse> = null;

      if (this.queryCache !== null && this.queryCache.isValid()) {
        return this.queryCache.getValue();
      }
      queryResult = new Promise<VoiceServerQueryResponse | null>((resolve, reject) => {
        Axios.get(this._parseQueryUrl())
          .then((response: AxiosResponse<VoiceServerQueryResponse>) => {
            resolve(response.data);
          }, (reason: any) => {
            reject(new QueryResponseError(`Error: Query call failed for serverId ${this.serverId}!`));
          })
          .catch(() => {
            reject(new QueryResponseError(`Error: Query call failed for serverId ${this.serverId}!`));
          });
      });

      this.queryCache = new Cache<Promise<VoiceServerQueryResponse>>(
        queryResult,
        Config.getCacheLifetime,
        () => this.query(),
      );

      return queryResult;
    }
  }

  /**
   * this call must be used to configure the visibility checks which is optional
   */
  export class ServerVisibilityOption {
    /**
     *
     * @param visibiltySelector
     * @param queryOnlyWhenVisible
     */
    constructor(visibiltySelector: JQuery, queryOnlyWhenVisible: boolean) {
      this._visibiltySelector = visibiltySelector;
      this._queryOnlyWhenVisible = queryOnlyWhenVisible;
    }

    /**
     * selector of the specified element which will be used, when {_queryOnlyWhenVisible} is turned on.
     * Will check if the element is physically displayed or not, if its visible he will send a request to the query engine
     *
     * @private
     */
    private _visibiltySelector: JQuery = null;

    /**
     * option that when turned on will only query informations about a server only if its physically displayed
     * on the page
     * @private
     */
    private _queryOnlyWhenVisible = false;

    /**
     * property which is used to track if the element was ever visible before
     * @private
     */
    private _wasVisible = false;

    /**
     * property which will inform if the element is currently visible
     * @private
     */
    private _isVisible = false;

    get isVisible(): boolean {
      return this._isVisible;
    }

    set isVisible(value: boolean) {
      this._isVisible = value;
    }

    get wasVisible(): boolean {
      return this._wasVisible;
    }

    set wasVisible(value: boolean) {
      this._wasVisible = value;
    }

    get visibiltySelector(): JQuery {
      return this._visibiltySelector;
    }

    set visibiltySelector(value: JQuery) {
      this._visibiltySelector = value;
    }

    get queryOnlyWhenVisible(): boolean {
      return this._queryOnlyWhenVisible;
    }

    set queryOnlyWhenVisible(value: boolean) {
      this._queryOnlyWhenVisible = value;
    }
  }

  /**
   * this class must be used when passing options to the initialization of a server
   */
  export class ServerOptions {
    /**
     * turns on / off the automatically fetchting of informations about the gameserver
     * @type {boolean}
     * @private
     */
    private _autoQuery = false;

    /**
     * sets the timer for automatically fetching informations (in ms)
     * @type {number}
     * @private
     */
    private _autoQueryTimer = 4000;

    /**
     * set a callback for getting informations from the server query
     * @private
     * @deprecated
     */
    private _autoQueryCallback: () => void;

    /**
     * a custom event which gets triggered
     * @private
     */
    private _queryEvent: ServerEventHandler<Promise<GameServerQueryResponse | VoiceServerQueryResponse>> = new ServerEventHandler();

    /**
     * set a callback which is called everytime the state was changed
     * @private
     */
    private _stateChangedCallback: (serverState: ServerStatus) => void;

    /**
     * used for combining the automatic query functionality and the visibilty component,
     * will basically check before getting query informations if the element is visible or was in the viewport
     * @private
     */
    private _visibiltyOption: ServerVisibilityOption = null;

    get visibiltyOption(): Server.ServerVisibilityOption {
      return this._visibiltyOption;
    }

    set visibiltyOption(value: Server.ServerVisibilityOption) {
      this._visibiltyOption = value;
    }

    /**
     *
     * @param autoQuery
     * @param autoQueryTimer
     */
    constructor(autoQuery: boolean, autoQueryTimer: number) {
      this._autoQuery = autoQuery;
      this._autoQueryTimer = autoQueryTimer;
    }

    get stateChangedCallback(): (serverState: Server.ServerStatus) => void {
      return this._stateChangedCallback;
    }

    set stateChangedCallback(value: (serverState: Server.ServerStatus) => void) {
      this._stateChangedCallback = value;
    }

    get autoQuery(): boolean {
      return this._autoQuery;
    }

    get autoQueryTimer(): number {
      return this._autoQueryTimer;
    }

    get autoQueryCallback(): (response: Promise<GameServerQueryResponse | VoiceServerQueryResponse>) => void {
      return this._autoQueryCallback;
    }

    get queryEvent(): ServerEventHandler<Promise<GameServerQueryResponse | VoiceServerQueryResponse>> {
      return this._queryEvent;
    }

    set queryEvent(value: ServerEventHandler<Promise<GameServerQueryResponse | VoiceServerQueryResponse>>) {
      this._queryEvent = value;
    }

    /**
     * will check if the autoQueryTimer has plausible values
     *
     * @return boolean
     */
    public isPlausibleTimer(): boolean {
      return this.autoQueryTimer !== null && this.autoQueryTimer > 0;
    }

    /**
     * will return true if the autoquery is turned on
     *
     * @return boolean
     */
    public isAutoQueryOn(): boolean {
      return this.autoQuery === true;
    }

    /**
     * will return true if the visibiltySelector is set (not null) and if he can find the element on the page
     *
     * @return boolean
     */
    public isLazyQueryActive(): boolean {
      return this.visibiltyOption !== null && this.visibiltyOption.visibiltySelector !== null && this.visibiltyOption.queryOnlyWhenVisible === true && this.visibiltyOption.visibiltySelector.length > 0;
    }
  }

  /**
   * this interface is used for the query responses of the voiceservers
   */
  export interface VoiceServerQueryResponse {
    online: number;
    slots: string;
    state: string;
  }

  /**
   * this interface is used for the query responses of the gameservers
   */
  export interface GameServerQueryResponse {
    online: boolean;
    name?: string;
    ipAddress?: string;
    port?: number;
    maxPlayers: number;
    currentPlayers: number;
    key?: string;
    queried?: string;
    extraPorts?: string;
  }

  /**
   * will be thrown when you try to use a method like the query for
   * informations that are not implemented (e.g. voiceserver does not has a name in the response)
   */
  class NotImplementedMethodError extends Error {
    /**
     *
     * @param {string} error
     */
    constructor(error: string) {
      super(error);

      Object.setPrototypeOf(this, QueryResponseError.prototype);
    }
  }

  /**
   * will be thrown when you try to retrive infos through {@link ServerUnit#getIp} built-in
   * functions like getName
   */
  class QueryInvalidTypeError extends Error {
    /**
     *
     * @param {string} error
     */
    constructor(error: string) {
      super(error);

      Object.setPrototypeOf(this, QueryResponseError.prototype);
    }
  }

  /**
   * will be thrown when there is something strange going on
   * with the responses from the API
   */
  class DeleteResponseError extends Error {
    /**
     *
     * @param {string} error
     */
    constructor(error: string) {
      super(error);

      Object.setPrototypeOf(this, QueryResponseError.prototype);
    }
  }

  /**
   * will be thrown when there is something strange going on
   * with the responses from the API
   */
  class QueryResponseError extends Error {
    /**
     *
     * @param {string} error
     */
    constructor(error: string) {
      super(error);

      Object.setPrototypeOf(this, QueryResponseError.prototype);
    }
  }

  /**
   * will be throwing when something is strange with the urls
   * from the backend
   */
  class MissingUrlError extends Error {
    /**
     *
     * @param {string} error
     */
    constructor(error: string) {
      super(error);

      Object.setPrototypeOf(this, MissingUrlError.prototype);
    }
  }

  /**
   * interface for all available window methods
   */
  export interface ServerWindow extends Window {
    getServers(): ServerInterface[];

    getServerByServiceId(serviceId: number): ServerInterface | null;

    getServerByServerId(): ServerInterface | null;

    getHelper(): ServerUnit;

    getFocusedServer(): ServerInterface;

    getServerInViewPort(): Array<ServerInterface>;
  }

  class ServerPositioning implements IServerPositioning {
    height: number;

    top: number;

    /**
     * @param height
     * @param top
     */
    constructor(height: number, top: number) {
      this.height = height;
      this.top = top;
    }
  }

  interface IServerPositioning {
    top: number;
    height: number;
  }

  export class Utilities {
    /**
     *
     * @type {boolean}
     */
    private static readonly DEBUG_MODE: boolean = true;

    /**
     *
     * @type {boolean}
     * @private
     */
    private static _visibiltyCheckInitialised = false;

    /**
     * the instance of the gportal websocket
     * @type {null}
     * @private
     */
    private static _websocketIOClient: SocketIOClient.Socket = null;

    /**
     * returns a array which uses the interface from {@link AxiosRequestConfig}
     * @returns {AxiosRequestConfig}
     * @private
     */
    public static getAjaxJSONOptions(): AxiosRequestConfig {
      return {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
          'X-Requested-By-Module': 'G-Portal Serverlib',
        },
      };
    }

    /**
     * print a debug message which will be outputed when DEBUG_MODE is true
     *
     * @param {string} message
     */
    public static debug(message: string) {
      if (Utilities.DEBUG_MODE === true && process.env.NODE_ENV !== 'production') {
        const currentDate: Date = new Date();
        const hour: number = currentDate.getUTCHours();
        const minutes: number = currentDate.getUTCMinutes();
        const seconds: number = currentDate.getUTCSeconds();

        console.log(`DEBUG [${hour}:${minutes}:${seconds}]    ${message}`);
      }
    }

    /**
     * returns a list of all servers
     * @param withCluster
     * @return {Array<ServerInterface>}
     */
    public static getServerList(withCluster: boolean): Array<ServerInterface> {
      const serverList: Array<ServerInterface> = [];

      for (const key in ServerUnit.instances) {
        const server: ServerInterface = ServerUnit.instances[key];

        if (ServerUnit.getActiveServer() !== null) {
          if (ServerUnit.getActiveServer().serverId === server.serverId) {
            serverList.push(server);

            if (withCluster && server.cluster && server.clusterServer.length) {
              for (const clusterKey in server.clusterServer) {
                const clusterServer: ServerInterface = server.clusterServer[clusterKey];
                serverList.push(clusterServer);
              }
            }
          }
        } else {
          serverList.push(server);
        }
      }

      return serverList;
    }

    /**
     * automatically registers websocket
     *
     * @private
     */
    public static registerWebsocket(): void {
      if (ServerUnit.hasGameservers()) {
        this._websocketIOClient = connect(`${window.socketURL}sensors`,
          {
            path: `/${window.regionPrefix}/ws/legacy/socket.io`,
            reconnectionAttempts: 7,
            reconnectionDelayMax: 7000,
            randomizationFactor: 0.7,
            transports: ['websocket', 'polling'],
          });
        Utilities.debug('websocket registered!');
        try {
          const serverList: ServerInterface[] = Utilities.getServerList(true);
          this._websocketIOClient.on('connect', () => {
            ServerUnit._websocketConnected();

            if ('serverUnitSelectedId' in window && typeof window.serverUnitSelectedId !== 'undefined') {
              this._websocketIOClient.emit('openService', window.serverUnitSelectedId);
              this._websocketIOClient.on('data', (type, id, data: IWebsocketState) => {
                Server.ServerUnit.getServerByServiceId2(window.serverUnitSelectedId).then((server: ServerInterface) => {
                  /**
                   * if the webresponse message a statechange, does the serverId match
                   * AND does the state change OR is it a installing state
                   */
                  if (type.toString() === ServerSocketTypes.STATECHANGE && id === server.serviceId
                    && (server.state.stateId !== data.state || server.state === ServerStatus.INSTALLING)) {
                    server.dispatchCustomEvent(type, id, data);
                    const eventData: IStateEvent = {
                      oldstate: server.state,
                      newstate: ServerStatus.getStateById(data.state),
                      details: data.details,
                    };

                    server.stateEvent.dispatchEvent(eventData);
                    server.state = ServerStatus.getStateById(data.state);
                  } else if (type.toString() === ServerSocketTypes.SENSORS) {
                    server.dispatchCustomEvent(type, id, data);
                  }
                });
              });

              Server.ServerUnit.getServerByServiceId2(window.serverUnitSelectedId).then((server: ServerInterface) => {
                if (server.cluster) {
                  server.clusterServer.forEach((subServer: ServerInterface) => {
                    this._websocketIOClient.emit('openService', subServer.serviceId);
                    this._websocketIOClient.on('data', (type2, id2, data2: IWebsocketState) => {
                      if (type2.toString() === ServerSocketTypes.STATECHANGE && id2 === subServer.serviceId && subServer.state.stateId !== data2.state) {
                        subServer.dispatchCustomEvent(type2, id2, data2);
                        const eventData: IStateEvent = {
                          oldstate: subServer.state,
                          newstate: ServerStatus.getStateById(data2.state),
                          details: data2.details,
                        };

                        subServer.stateEvent.dispatchEvent(eventData);
                        subServer.state = ServerStatus.getStateById(data2.state);
                      } else if (type2.toString() === ServerSocketTypes.SENSORS) {
                        subServer.dispatchCustomEvent(type2, id2, data2);
                      }
                    });
                  });
                }
              });
            } else {
              /* MULTIPLE SERVERS for example Gamecloud Dashboard */
              // Utilities.debug('Setting up websocket for multiple server!');
              for (const key in serverList) {
                const server: ServerInterface = serverList[key];
                this._websocketIOClient.emit('openService', server.serviceId);
              }

              /* get multiple services with one on event */
              this._websocketIOClient.on('data', (type, id, data: IWebsocketState) => {
                for (const key in serverList) {
                  const server = serverList[key];
                  if (type === ServerSocketTypes.STATECHANGE && id === server.serviceId) {
                    if (ServerStatus.isValidStatus(data.state)
                      && (server.state.stateId !== data.state || server.state === ServerStatus.INSTALLING)) {
                      const eventData: IStateEvent = {
                        oldstate: server.state,
                        newstate: ServerStatus.getStateById(data.state),
                        details: data.details,
                      };

                      server.stateEvent.dispatchEvent(eventData);
                      server.state = ServerStatus.getStateById(data.state);
                    }
                  }
                }
              })
                .on('disconnect', () => {
                  Utilities._websocketDisconnected();
                });
            }
          });
        } catch (e) {
          console.log(e);
        }

        window.addEventListener('beforeunload', (ev) => {
          this._websocketIOClient.close();
        });
      }
    }

    /**
     * will be called when the websocket gets disconnected
     * @private
     */
    private static _websocketDisconnected(): void {
      Utilities.debug('Websocket disconnected!');
    }

    public static getServerPositioning(): Array<ServerPositioning> {
      const positioningList: Array<ServerPositioning> = [];

      for (const index in ServerUnit.instances) {
        const server: ServerInterface = ServerUnit.instances[index];

        if (server.options.isLazyQueryActive() && typeof server.options.visibiltyOption !== 'undefined') {
          const visibiltyOption: ServerVisibilityOption = server.options.visibiltyOption;
          const visibiltyElement: JQuery = $(visibiltyOption.visibiltySelector);

          const hT: number = visibiltyElement.offset().top;
          const hH: number = visibiltyElement.outerHeight();

          positioningList.push(new ServerPositioning(hT, hH));
        }
      }

      return positioningList;
    }

    /**
     * will register the window.scroll for lazy loading server query requests
     *
     * @return void
     */
    public static registerLazyServerQuery(): void {
      /* is not a single server page GP-1677 */
      window.addEventListener(ServerEventType.GAMESERVERINITIALISED_EVENT, () => {
        if (Utilities._visibiltyCheckInitialised) {
          return Utilities.debug('Error: registerLazyServerQuery was already initialised!');
        }

        if (ServerUnit.getActiveServer() === null && !Utilities._visibiltyCheckInitialised) {
          Utilities.debug('registerVisibiltyCheck');

          if (ServerUnit.hasGameservers() && ServerUnit.instances.length) {
            Utilities._visibiltyCheckInitialised = true;

            $(window).scroll(function () {
              // Utilities.debug('scrolled');

              let visibleServers = 0;

              const wH: number = $(window).height();
              const wS: number = $(this).scrollTop();

              for (let i = 0; i < ServerUnit.instances.length; i++) {
                const server: ServerInterface = ServerUnit.instances[i];

                if (server.options.isLazyQueryActive() && typeof server.options.visibiltyOption !== 'undefined') {
                  const visibiltyElement: JQuery = server.options.visibiltyOption.visibiltySelector;

                  const hT: number = visibiltyElement.offset().top;
                  const hH: number = visibiltyElement.outerHeight();

                  if (wS > (hT + hH - wH) && (hT > wS) && (wS + wH > hT + hH)) {
                    visibleServers++;

                    server.options.visibiltyOption.wasVisible = true;
                    server.options.visibiltyOption.isVisible = true;
                  } else {
                    server.options.visibiltyOption.isVisible = false;
                  }
                }
              }
            });
          }
        }
      });
    }

    public static registerServerInstances() {
      function createServerInstanceFromArray(data) {
        let serverInstance;

        const options = {
          serverId: Number(data.serverId),
          serviceId: Number(data.serviceId),
          type: data.type,
          links: [], // not used anymore ?
        };
        switch (data.type) {
          case ServerType.VOICESERVER:
            return new Voiceserver(options);
          case ServerType.GAMESERVER:
          case ServerType.NODESERVER:
            return new Gameserver(options);
        }
        throw `Invalide servertype: ${data.type}`;
      }

      window.addEventListener(Server.ServerEventType.GAMESERVERQUERY_EVENT, (event: CustomEvent<any>) => {
        const serverInstances = {};
        const nodeServers = [];

        /*
         a server from request looks like this:
         {
         "serverId": 2758981,
         "serviceId": 2833897,
         "type": "nodeserver",
         "masterServer": 2758966
         },
         In case of an Atlas nodeserver there is masterServerId given.
         */

        // create all instances from data
        for (const index in event.detail) {
          const server = event.detail[index];

          // collect node serversId for later
          if (ServerType.NODESERVER == server.type) {
            nodeServers.push({
              nodeServerId: server.serverId,
              masterServerId: server.masterServer,
            });
          }
          serverInstances[server.serverId] = createServerInstanceFromArray(server);
        }

        // assign node servers to their masters
        for (let i = 0, l = nodeServers.length; i < l; i++) {
          const nodeServerId = nodeServers[i].nodeServerId;
          const masterServerId = nodeServers[i].masterServerId;
          const nodeServer = serverInstances[nodeServerId];
          const masterServer = serverInstances[masterServerId];
          masterServer.cluster = true;
          masterServer.clusterServer.push(nodeServer);
          delete serverInstances[nodeServerId];
        }

        for (const index in serverInstances) {
          ServerUnit.instances.push(serverInstances[index]);
        }

        window.dispatchEvent(
          new CustomEvent(ServerEventType.GAMESERVERINITIALISED_EVENT),
        );
        Utilities.registerWebsocket();
      });
    }
  }

  export namespace ServerAPI {

  }

  export class Api {
    readonly API_URL = 'de/api/gameserver/';

    private static _instance: Api = null;

    static get instance(): Server.Api {
      return this._instance;
    }

    static set instance(value: Server.Api) {
      this._instance = value;
    }

    public static getInstance(): Api {
      if (Api.instance !== null) {
        return Api.instance;
      }

      Api.instance = new Api();
      Api.instance._fetchServer();

      return Api.instance;
    }

    /**
     *
     * @private
     */
    protected _fetchServer(): void {
      if (window.fetchServersForMenu) {
        // menuUrl
        // servicesUrl
        const promise: Promise<any> = new Promise((resolve, reject) => {
          API.get(window.servicesUrl).then((data) => {
            resolve(data);
          }).catch((error) => {
            reject(error);
          });
        });

        promise.then((response) => {
          let data = response.data;
          if (!data.some((el) => {el.serverId === window.gameserverId})) {
            data.push({
              serverId: window.gameserverId,
              serviceId: window.serverUnitSelectedId,
              type: "gameserver",
              masterServer: null
            });
          }
          window.dispatchEvent(
            new CustomEvent(
              Server.ServerEventType.GAMESERVERQUERY_EVENT, {
                detail: response.data,
              },
            ),
          );
        }).catch((error) => {
          console.error(error);
        });
      }
    }
  }

  /**
   * will be only used internally for creating voice- and gameserver
   */
  export function initServer() {
    /* this will create a window dispatch for every gameserver from the InterfaceAPIController */
    Api.getInstance();

    /* this will initialize every voice and gameserver plus cluster */
    Utilities.registerServerInstances();
  }
}

declare let window: Server.ServerWindow;

window.getServerInViewPort = () => Server.ServerUnit.getServerInViewPort();

window.getServers = () => Server.ServerUnit.instances;
window.getFocusedServer = () => Server.ServerUnit.getActiveServer();
window.getServerByServiceId = (serviceId: number) => Server.ServerUnit.getServerByServiceId(serviceId);
